import { AIModelInfo, AIUseCase } from '../../../services/settingsService';

export interface UseCaseMeta {
  label: string;
  /** Compact label for the redesigned settings UI */
  shortLabel: string;
  description: string;
  /** Business cards are photos, so the model must read images */
  needsImage: boolean;
  /** Typical tokens for one use, for cost estimates */
  estimate: { input: number; output: number; unit: string };
}

export const USE_CASE_META: Record<AIUseCase, UseCaseMeta> = {
  businessCard: {
    label: 'OCR and scanning',
    shortLabel: 'OCR',
    description: 'Reading business cards and documents from an image',
    needsImage: true,
    estimate: { input: 2500, output: 500, unit: 'scan' },
  },
  writing: {
    label: 'Text writing',
    shortLabel: 'Writing',
    description: 'Drafting text for a person to read: briefs, digests, wiki pages',
    needsImage: false,
    estimate: { input: 2000, output: 1000, unit: 'request' },
  },
  search: {
    label: 'Web search',
    shortLabel: 'Search',
    description: 'Collecting news and monitoring signals from the web',
    needsImage: false,
    estimate: { input: 6000, output: 6000, unit: 'search' },
  },
  research: {
    label: 'Research',
    shortLabel: 'Research',
    description: 'Long multi-step work: intelligence reports, mapping the state',
    needsImage: false,
    estimate: { input: 15000, output: 4000, unit: 'report' },
  },
  analysis: {
    label: 'Analysis',
    shortLabel: 'Analysis',
    description: 'Reasoning over what the directory already holds',
    needsImage: false,
    estimate: { input: 8000, output: 1500, unit: 'request' },
  },
};

export const MODEL_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:/@+-]{0,199}$/;

/** Price per 1M tokens, e.g. "$0.95" or "$0.075" */
export function formatPrice(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (value === 0) return 'Free';
  if (value < 0.1) return `$${trimZeros(value.toFixed(3))}`;
  return `$${value.toFixed(2)}`;
}

/** Small dollar amounts, e.g. "$0.0031" */
export function formatCost(value: number | null): string {
  if (value === null) return '—';
  if (value === 0) return 'Free';
  if (value < 0.0001) return '<$0.0001';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  if (value < 1) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(2)}`;
}

/** 1048576 → "1M", 262144 → "262K" */
export function formatTokens(value: number | null | undefined): string {
  if (!value) return '—';
  if (value >= 1_000_000) return `${trimZeros((value / 1_048_576).toFixed(1))}M`;
  if (value >= 10_000) return `${Math.round(value / 1000)}K`;
  if (value >= 1000) return `${trimZeros((value / 1000).toFixed(1))}K`;
  return String(value);
}

/** "2026-07-21" → "Jul 2026" */
export function formatReleaseDate(value: string | null): string {
  if (!value) return '—';
  const [year, month] = value.split('-');
  if (!month) return year;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

export function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

/** Estimated cost of one typical use of the model, or null without pricing. */
export function estimateUseCost(model: AIModelInfo, useCase: AIUseCase): number | null {
  const { input, output } = model.pricing ?? {};
  if (input === null || input === undefined || output === null || output === undefined) {
    return null;
  }
  const { estimate } = USE_CASE_META[useCase];
  return (estimate.input * input + estimate.output * output) / 1_000_000;
}

/** Whether the model can be used for the use case; unknown capability is allowed. */
export function compatibility(
  model: AIModelInfo,
  useCase: AIUseCase,
): { compatible: boolean; reason?: string } {
  if (model.category !== 'chat') {
    return { compatible: false, reason: 'Not a text generation model' };
  }
  if (USE_CASE_META[useCase].needsImage && model.capabilities.imageInput === false) {
    return { compatible: false, reason: 'Cannot read images' };
  }
  return { compatible: true };
}

function trimZeros(value: string): string {
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}
